/** Suggestions under the mail search box (2026-10-02), Outlook's "emails
 *  from Carolyn McDonald" and more: as the end of the query is typed, offer
 *  what it most likely means.
 *
 *    carolyn       Emails from / sent to / with Carolyn McDonald (people
 *                  you mail with, best first), subject contains, operators
 *                  and keywords the word starts ("fr" → from:, "unr" →
 *                  is:unread, "last" → received:"last week")
 *    to: caro      people only, as "Emails sent to …"
 *    is: / has:    the flags; received: before: after: the date words;
 *    in:           folders; category: categories
 *    (empty)       recent searches, then a few operators to start from
 *
 *  Picking one rewrites the end of the query (`to: caro` →
 *  `to:"Carolyn McDonald"`) and, unless it is an operator still waiting for
 *  a value, runs it. Pure; the box fetches the people. */
import {
  DATE_WORDS,
  FOLDER_ALIASES,
  HAS_VALUES,
  IS_VALUES,
  SEARCH_OPERATORS,
  describeMailSearch,
  formatSearchTerm,
  interpretNaturalSearch,
  parseDateSpan,
  parseMailSearch,
  tokenizeMailSearch,
  type RawToken,
} from "@waki/wov-contract/mail-search";

export type SuggestionIcon = "smart" | "saved" | "from" | "to" | "with" | "cc" | "subject" | "search" | "operator" | "flag" | "attachment" | "date" | "folder" | "category" | "recent";

export type SearchSuggestion = {
  id: string;
  icon: SuggestionIcon;
  /** Muted words before the main part ("Emails from"). */
  lead: string;
  /** The main part, in bold ("Carolyn McDonald"). */
  strong: string;
  /** Muted, right-aligned (an address, an example). */
  hint?: string;
  /** The whole query after picking it. */
  query: string;
  /** Run the search; false for an operator still waiting for its value. */
  run: boolean;
};

export type SuggestPerson = { name: string; address: string };

export type SuggestContext = {
  people: SuggestPerson[];
  /** What the AI made of a sentence the patterns did not understand
   *  (POST /api/mail/search/understand), for this input. */
  ai?: { query: string; description: string } | null;
  /** Saved searches: first in an empty box, and matched by name. */
  saved?: Array<{ name: string; query: string }>;
  recent: string[];
  folders: string[];
  categories: string[];
  /** "YYYY-MM-DD", for recognising dates. */
  today: string;
  /** The folder on screen, for "Search <folder> for …" (2026-10-09). */
  scope?: { path: string; name: string } | null;
};

const PERSON_OPS: Record<string, { op: string; lead: string; icon: SuggestionIcon }> = {
  from: { op: "from", lead: "Emails from", icon: "from" },
  sender: { op: "from", lead: "Emails from", icon: "from" },
  to: { op: "to", lead: "Emails sent to", icon: "to" },
  recipient: { op: "to", lead: "Emails sent to", icon: "to" },
  recipients: { op: "to", lead: "Emails sent to", icon: "to" },
  cc: { op: "cc", lead: "Emails copied to", icon: "cc" },
  bcc: { op: "bcc", lead: "Emails bcc'd to", icon: "cc" },
  with: { op: "with", lead: "Emails with", icon: "with" },
  participant: { op: "with", lead: "Emails with", icon: "with" },
  participants: { op: "with", lead: "Emails with", icon: "with" },
  people: { op: "with", lead: "Emails with", icon: "with" },
  involving: { op: "with", lead: "Emails with", icon: "with" },
};
const DATE_OPS = new Set(["received", "sent", "on", "date", "when", "during", "after", "since", "before", "until"]);
const FOLDER_OPS = new Set(["in", "folder"]);
const CATEGORY_OPS = new Set(["category", "categories", "cat", "label", "tag"]);
const SUBJECT_OPS = new Set(["subject", "about", "title"]);

/** The end of the query the suggestions are about: the last token when
 *  the query does not end in a space, or an operator still empty ("to: "),
 *  or the run of plain words at the end ("carolyn mc"), which is what a
 *  name lookup uses. */
export function searchTail(input: string): { start: number; op: string | null; value: string; negate: boolean } | null {
  if (!input.trim()) return null;
  const toks = tokenizeMailSearch(input);
  const last = toks[toks.length - 1];
  if (!last) return null;
  const endsInSpace = /\s$/.test(input);
  if (last.op) {
    // "to: " is still waiting for its value; "to:carolyn " is done.
    if (endsInSpace && last.value) return null;
    return { start: last.start, op: last.op, value: last.value, negate: last.negate };
  }
  if (endsInSpace) return null;
  // Plain words at the end, up to three, for "carolyn mc".
  const run: RawToken[] = [];
  for (let i = toks.length - 1; i >= 0 && run.length < 3; i--) {
    const t = toks[i];
    if (t.op || t.quoted || t.negate) break;
    run.unshift(t);
  }
  if (!run.length) return { start: last.start, op: null, value: last.value, negate: last.negate };
  return { start: run[0].start, op: null, value: run.map((t) => t.value).join(" "), negate: false };
}

/** The first person term of a query (from:, to:, cc:, with:), if any. */
function firstPersonTerm(q: string): { start: number; end: number; op: string; value: string } | null {
  for (const t of tokenizeMailSearch(q)) {
    if (t.op && PERSON_OPS[t.op] && t.value && !t.negate) return { start: t.start, end: t.end, op: PERSON_OPS[t.op].op, value: t.value };
  }
  return null;
}

/** The text to look people up with, or null. For a sentence the box
 *  understood ("from carol last week"), the person in it. */
export function peopleQueryFor(input: string, today?: string): string | null {
  if (today) {
    const smart = understoodSentence(input.trim(), today);
    if (smart) {
      const pt = firstPersonTerm(smart.query);
      return pt && !SELF_PEOPLE.has(pt.value.toLowerCase()) ? pt.value : null;
    }
  }
  const t = searchTail(input);
  if (!t) return null;
  if (t.op && !PERSON_OPS[t.op]) return null;
  const v = t.value.trim();
  return v.length >= (t.op ? 1 : 2) ? v : null;
}

const personLabel = (p: SuggestPerson) => p.name || p.address;

function prefixMatch(word: string, candidate: string, min = 2): boolean {
  const w = word.toLowerCase();
  return w.length >= min && candidate.toLowerCase().startsWith(w) && candidate.toLowerCase() !== w;
}

/** Where to look (2026-10-09): after "Search all mail for …", the same
 *  words in the folder on screen and in Sent Items; in an empty box, those
 *  two as starting points. Not while an operator is being typed, and not
 *  when the search already says `in:`. */
export function buildSearchSuggestions(input: string, ctx: SuggestContext): SearchSuggestion[] {
  const out = baseSuggestions(input, ctx);
  const trimmed = input.trim();
  const tail = trimmed ? searchTail(input) : null;
  if (tail?.op) return out;
  if (out[0]?.icon === "smart") return out; // a sentence, understood: its reading is the search
  if (trimmed && out.some((o) => !o.run)) return out; // still completing a word into an operator
  if (parseMailSearch(trimmed, ctx.today).terms.some((t) => t.kind === "folder")) return out;
  const here = ctx.scope && !/^sent items$/i.test(ctx.scope.path) ? ctx.scope : null;
  const scopes = [...(here ? [{ term: folderInTerm(here.path), name: here.name, hint: "this folder" }] : []), { term: "in:sent", name: "Sent Items", hint: "what you sent" }];
  for (const sc of scopes) {
    const query = trimmed ? `${sc.term} ${trimmed}` : `${sc.term} `;
    if (out.some((o) => o.query === query)) continue;
    out.push(
      trimmed
        ? { id: `${out.length}:${query}`, icon: "folder", lead: `Search ${sc.name} for`, strong: `“${trimmed}”`, hint: sc.hint, query, run: true }
        : { id: `${out.length}:${query}`, icon: "folder", lead: "Search in", strong: sc.name, hint: sc.hint, query, run: false },
    );
  }
  return out;
}

/** `in:` for a folder path: the short alias for the well-known ones. */
export function folderInTerm(path: string): string {
  const aliases: Record<string, string> = { inbox: "inbox", "sent items": "sent", "deleted items": "deleted", drafts: "drafts", "junk email": "junk", archive: "archive" };
  const a = aliases[path.trim().toLowerCase()];
  if (a) return `in:${a}`;
  return formatSearchTerm("in", path);
}

function baseSuggestions(input: string, ctx: SuggestContext): SearchSuggestion[] {
  const out: SearchSuggestion[] = [];
  const push = (s: Omit<SearchSuggestion, "id">) => {
    if (out.some((o) => o.query === s.query)) return;
    out.push({ ...s, id: `${out.length}:${s.query}` });
  };

  if (!input.trim()) {
    for (const sv of (ctx.saved ?? []).slice(0, 8)) push({ icon: "saved", lead: "", strong: sv.name, hint: sv.query, query: sv.query, run: true });
    for (const r of ctx.recent.slice(0, 6)) push({ icon: "recent", lead: "", strong: r, query: r, run: true });
    for (const o of SEARCH_OPERATORS.slice(0, 7)) {
      const q = o.example.endsWith(":") ? o.example : `${o.op}:`;
      push({ icon: "operator", lead: "Search by", strong: `${o.op}:`, hint: o.hint, query: q, run: false });
    }
    return out;
  }

  const trimmed = input.trim();
  // A sentence: "from carolyn last week about budget", understood.
  const smart = understoodSentence(trimmed, ctx.today) ?? (ctx.ai ? { query: ctx.ai.query, description: ctx.ai.description } : null);
  // Saved searches by name.
  const lc = trimmed.toLowerCase();
  const savedHits = (ctx.saved ?? []).filter((sv) => lc.length >= 2 && (sv.name.toLowerCase().includes(lc) || sv.query.toLowerCase().includes(lc)));
  if (smart) {
    // The sentence as understood, then with its person resolved to the
    // people you mail with ("carol" → Carolyn McDonald, Carol Hu).
    push({ icon: "smart", lead: "", strong: smart.description, hint: smart.query, query: smart.query, run: true });
    const pt = firstPersonTerm(smart.query);
    if (pt && !SELF_PEOPLE.has(pt.value.toLowerCase())) {
      for (const p of ctx.people.slice(0, 3)) {
        const who = personLabel(p);
        if (who.toLowerCase() === pt.value.toLowerCase()) continue;
        const q2 = smart.query.slice(0, pt.start) + formatSearchTerm(pt.op, who) + smart.query.slice(pt.end);
        push({ icon: "smart", lead: "", strong: describeMailSearch(parseMailSearch(q2, ctx.today)), hint: p.name ? p.address : undefined, query: q2, run: true });
      }
    }
    for (const sv of savedHits) push({ icon: "saved", lead: "Saved:", strong: sv.name, hint: sv.query, query: sv.query, run: true });
    push({ icon: "search", lead: "Search for the exact words", strong: `“${trimmed}”`, query: trimmed, run: true });
    return out;
  }
  for (const sv of savedHits) push({ icon: "saved", lead: "Saved:", strong: sv.name, hint: sv.query, query: sv.query, run: true });
  const tail = searchTail(input);
  if (!tail) {
    push({ icon: "search", lead: "Search for", strong: trimmed, query: trimmed, run: true });
    return out;
  }
  const head = input.slice(0, tail.start);
  const neg = tail.negate ? "-" : "";
  const value = tail.value.trim();
  const vlc = value.toLowerCase();

  if (tail.op) {
    const person = PERSON_OPS[tail.op];
    if (person) {
      for (const p of ctx.people.slice(0, 6)) {
        push({ icon: person.icon, lead: tail.negate ? `Not ${person.lead.toLowerCase()}` : person.lead, strong: personLabel(p), hint: p.name ? p.address : undefined, query: head + formatSearchTerm(person.op, personLabel(p), tail.negate), run: true });
      }
      if (value) push({ icon: person.icon, lead: person.lead, strong: `“${value}”`, query: head + formatSearchTerm(person.op, value, tail.negate), run: true });
      return out;
    }
    if (tail.op === "is" || tail.op === "has") {
      const list = tail.op === "is" ? IS_VALUES.map((v) => ({ value: v.value, label: v.label })) : HAS_VALUES.map((v) => ({ value: v.value, label: v.label }));
      for (const v of list) {
        if (vlc && !v.value.startsWith(vlc) && !v.label.toLowerCase().includes(vlc)) continue;
        push({ icon: v.value === "attachment" ? "attachment" : "flag", lead: "", strong: v.label, hint: `${tail.op}:${v.value}`, query: `${head}${neg}${tail.op}:${v.value}`, run: true });
      }
      return out;
    }
    if (DATE_OPS.has(tail.op)) {
      const lead = tail.op === "after" || tail.op === "since" ? "Since" : tail.op === "before" || tail.op === "until" ? "Before" : "Received";
      for (const w of DATE_WORDS) {
        if (vlc && !w.startsWith(vlc)) continue;
        push({ icon: "date", lead, strong: w, query: head + formatSearchTerm(tail.op, w, tail.negate), run: true });
      }
      const span = value ? parseDateSpan(value, ctx.today) : null;
      if (span && !DATE_WORDS.includes(vlc)) push({ icon: "date", lead, strong: span.label, query: head + formatSearchTerm(tail.op, value, tail.negate), run: true });
      return out;
    }
    if (FOLDER_OPS.has(tail.op)) {
      const seen = new Set<string>();
      for (const [alias, path] of Object.entries(FOLDER_ALIASES)) {
        if (seen.has(path) || (vlc && !alias.startsWith(vlc))) continue;
        seen.add(path);
        push({ icon: "folder", lead: "In", strong: path, query: head + formatSearchTerm(tail.op, alias, tail.negate), run: true });
      }
      for (const f of ctx.folders) {
        if (out.length >= 10) break;
        if (!vlc || !f.toLowerCase().includes(vlc) || seen.has(f)) continue;
        push({ icon: "folder", lead: "In", strong: f, query: head + formatSearchTerm(tail.op, f, tail.negate), run: true });
      }
      return out;
    }
    if (CATEGORY_OPS.has(tail.op)) {
      for (const c of ctx.categories) {
        if (out.length >= 10) break;
        if (vlc && !c.toLowerCase().replace(/^_p_/, "").includes(vlc.replace(/^_p_/, ""))) continue;
        push({ icon: "category", lead: "Category", strong: c.replace(/^_p_/, ""), hint: c, query: head + formatSearchTerm(tail.op, c, tail.negate), run: true });
      }
      return out;
    }
    if (SUBJECT_OPS.has(tail.op) && value) {
      push({ icon: "subject", lead: "Subject has", strong: `“${value}”`, query: head + formatSearchTerm("subject", value, tail.negate), run: true });
      return out;
    }
    push({ icon: "search", lead: "Search for", strong: trimmed, query: trimmed, run: true });
    return out;
  }

  // Plain words at the end.
  const lastWord = value.split(/\s+/).pop() ?? "";
  const headWithWords = (term: string) => head + term;
  // An operator the word starts: "fr" → from:
  for (const o of SEARCH_OPERATORS) {
    if (!prefixMatch(lastWord, o.op) || value.includes(" ")) continue;
    push({ icon: "operator", lead: "Search by", strong: `${o.op}:`, hint: o.hint, query: headWithWords(`${o.op}:`), run: false });
  }
  // People: the best match three ways, the next two as sender and recipient.
  ctx.people.slice(0, 3).forEach((p, i) => {
    const who = personLabel(p);
    push({ icon: "from", lead: "Emails from", strong: who, hint: p.name ? p.address : undefined, query: headWithWords(formatSearchTerm("from", who)), run: true });
    push({ icon: "to", lead: "Emails sent to", strong: who, hint: p.name ? p.address : undefined, query: headWithWords(formatSearchTerm("to", who)), run: true });
    if (i === 0) push({ icon: "with", lead: "Emails with", strong: who, hint: "from, to or cc", query: headWithWords(formatSearchTerm("with", who)), run: true });
  });
  // Keywords: "unr" → is:unread, "attach" → has:attachment.
  if (!value.includes(" ") && lastWord.length >= 3) {
    for (const v of IS_VALUES) {
      if (prefixMatch(lastWord, v.value, 3)) push({ icon: "flag", lead: "", strong: v.label, hint: `is:${v.value}`, query: headWithWords(`is:${v.value}`), run: true });
    }
    if (prefixMatch(lastWord, "attachments", 3) || prefixMatch(lastWord, "files", 3)) {
      push({ icon: "attachment", lead: "", strong: "Has attachments", hint: "has:attachment", query: headWithWords("has:attachment"), run: true });
    }
  }
  // Date words: "yest", "last w", "this m".
  for (const w of DATE_WORDS) {
    if (prefixMatch(value, w, 3)) push({ icon: "date", lead: "Received", strong: w, query: headWithWords(formatSearchTerm("received", w)), run: true });
  }
  push({ icon: "subject", lead: "Subject has", strong: `“${value}”`, query: headWithWords(formatSearchTerm("subject", value)), run: true });
  push({ icon: "search", lead: "Search all mail for", strong: `“${trimmed}”`, query: trimmed, run: true });
  return out;
}

/** Which suggestion Enter takes without choosing: the understood sentence
 *  when there is one (typing "from carolyn last week" and Enter should do
 *  that), else none (the box as typed). */
export function defaultSuggestionIndex(list: SearchSuggestion[]): number {
  return list.length > 0 && list[0].icon === "smart" ? 0 : -1;
}

/** The query in words, for titles and saved-search names. */
export function describeQuery(q: string, today: string): string {
  return describeMailSearch(parseMailSearch(q, today)) || q;
}

const RECENT_KEY = "wov.mail.recentSearches";

export function readRecentSearches(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
}

export function rememberSearch(q: string): void {
  const t = q.trim();
  if (!t) return;
  try {
    const next = [t, ...readRecentSearches().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("wov:recent-searches"));
  } catch {
    /* storage unavailable */
  }
}

/** A sentence worth asking the AI about: four words or more, no operators,
 *  and nothing the built-in patterns understood (or only half of it). */
export function wantsAiUnderstanding(input: string, today: string): boolean {
  const t = input.trim();
  if (t.split(/\s+/).length < 4) return false;
  if (tokenizeMailSearch(t).some((x) => x.op)) return false;
  return understoodSentence(t, today) === null;
}

/** People the daemon resolves itself, never looked up by name. */
const SELF_PEOPLE = new Set(["me", "manager"]);

/** Words a search that understood the sentence would not still hold. */
const LOOSE_WORDS = new Set([
  "anything", "something", "everything", "stuff", "things", "people", "someone", "anyone", "everyone", "from", "to", "with",
  "did", "does", "do", "say", "said", "says", "share", "shared", "what", "who", "when", "how", "me", "you", "we", "they",
  "asked", "told", "need", "needs", "still",
]);

/** The built-in patterns' reading of a sentence, unless it left much of
 *  it as plain words ("show me the slides people shared in september" →
 *  `received:september slides people shared`): that is for the AI. */
export function understoodSentence(input: string, today: string): { query: string; description: string } | null {
  const local = interpretNaturalSearch(input, today);
  if (!local) return null;
  const left = parseMailSearch(local.query, today)
    .terms.filter((t) => t.kind === "text" && !t.negate)
    .flatMap((t) => t.value.toLowerCase().split(/\s+/));
  const loose = left.length >= 4 || (left.length >= 2 && left.some((w) => LOOSE_WORDS.has(w)));
  return loose ? null : local;
}

/** Whether an `in:` term names the folder a query searches. */
export function sameFolder(term: string, folder: string | null): boolean {
  if (!folder) return false;
  const canon = (v: string) => {
    const x = v.replace(/^in:/i, "").replace(/"/g, "").trim().toLowerCase();
    return (FOLDER_ALIASES[x] ?? x).toLowerCase();
  };
  return canon(term) === canon(folder);
}

/** The query with its folder terms (in:, folder:) replaced by `term`, or
 *  removed when `term` is empty: the Where chip (2026-10-09). */
export function replaceFolder(query: string, term: string): string {
  let out = query;
  const toks = tokenizeMailSearch(query).filter((t) => t.op && /^(in|folder)$/i.test(t.op));
  for (const t of [...toks].sort((a, b) => b.start - a.start)) out = out.slice(0, t.start) + out.slice(t.end);
  out = out.replace(/\s+/g, " ").trim();
  return term ? `${term} ${out}`.trim() : out;
}
